'use client';

import React from 'react';
import { Flex, Box, Heading, Text, VStack, Divider, type BoxProps } from '@shared/ui';

interface SectionItemProps extends BoxProps {
    title?: string;
    description?: string;
    hint?: React.JSX.Element | string;
    children: React.ReactNode;
    noPadding?: boolean;
    enableHeaderDivider?: boolean;
    useVerticalGrow?: boolean;
}

export const SectionItem: React.FC<SectionItemProps> = (props) => {
    const {
        children,
        title,
        description,
        hint,
        noPadding,
        enableHeaderDivider = true,
        useVerticalGrow = false,
        ...restProps
    } = props;

    return (
        <Box
            position="relative"
            flexGrow={useVerticalGrow ? 1 : 0}
            flexShrink="1"
            flexBasis="auto"
            as="section"
            bg="neutral.000"
            w="100%"
            borderRadius="lg"
        >
            {(title || description) && (
                <>
                    <VStack
                        // 有分隔線才有需要與下方區塊推出距離
                        pb={enableHeaderDivider ? 's' : 'none'}
                        pt="s"
                        px={{
                            base: 's',
                            md: 'm',
                        }}
                        spacing="xxs"
                        alignItems="start"
                    >
                        {title && (
                            <Flex
                                w="100%"
                                columnGap="xs"
                                flexDirection="row"
                                alignItems="center"
                            >
                                <Heading
                                    as="h1"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    lineHeight="1.5"
                                >
                                    {title}
                                </Heading>
                                {hint}
                            </Flex>
                        )}
                        {description && (
                            <Text color="neutral.080" fontSize="base">
                                {description}
                            </Text>
                        )}
                    </VStack>
                    {enableHeaderDivider && (
                        <Box px="m">
                            <Divider boxSizing="border-box" color="gray.200" />
                        </Box>
                    )}
                </>
            )}
            <VStack
                alignItems="stretch"
                py={noPadding ? 0 : 'm'}
                px={noPadding ? 0 : { base: 's', md: 'm' }}
                {...restProps}
            >
                {children}
            </VStack>
        </Box>
    );
};

export type SectionItemReturn = ReturnType<typeof SectionItem>;
